import React, { useContext, useState, useEffect } from "react";
import { Container, Spinner, Badge, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Bell,
  Car,
  Wallet,
  Tag,
  Star,
  Headphones,
  ArrowLeft,
} from "lucide-react";
import AppContext from "../../../context/AppContext";
import "../../../assets/css/Customer/Notification/NotificationList.css";

const NotificationList = () => {
  const navigate = useNavigate();
  const { baseUrl, authToken } = useContext(AppContext);

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  // Category -> icon + color mapping (Trustidrive specific)
  const getCategoryStyle = (category) => {
    switch (category) {
      case "ride":
        return { icon: <Car size={18} color="#ffffff" />, color: "#1B9B82" };
      case "wallet":
        return { icon: <Wallet size={18} color="#ffffff" />, color: "#f59e0b" };
      case "offer":
        return { icon: <Tag size={18} color="#ffffff" />, color: "#8b5cf6" };
      case "rating":
        return { icon: <Star size={18} color="#ffffff" />, color: "#FFB800" };
      case "ticket":
        return {
          icon: <Headphones size={18} color="#ffffff" />,
          color: "#3b82f6",
        };
      default:
        return { icon: <Bell size={18} color="#ffffff" />, color: "#1B9B82" };
    }
  };

  const viewAllNotification = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/customer/Notification/viewAllNotification`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success === true) {
          const updatedNotifications = response.data.notifications.map(
            (item) => {
              const { icon, color } = getCategoryStyle(item.category);
              return { ...item, icon, color };
            },
          );
          setNotifications(updatedNotifications);
        } else if (response.data.success === false) {
          setErrorMessage(response.data.message);
        }
      })
      .catch((error) => {
        console.log(error);
        setErrorMessage("Unable to load notifications right now.");
      })
      .finally(() => setLoading(false));
  };

  const markAsRead = (id) => {
    // Optimistic UI update
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n)),
    );
    axios
      .post(
        `${baseUrl}/customer/Notification/markAsRead/${id}`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } },
      )
      .catch((error) => console.log(error));
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
    axios
      .post(
        `${baseUrl}/customer/Notification/markAllAsRead`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } },
      )
      .catch((error) => console.log(error));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    viewAllNotification();
  }, []);

  return (
    <div className="NotificationList-main-container">
      <Container fluid className="NotificationList-content px-3">
        {/* Header row */}
        <div className="NotificationList-top-row">
          <button
            className="NotificationList-back-btn"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={20} color="#000000" />
          </button>
          <h2 className="NotificationList-title">Notifications</h2>
          <div className="NotificationList-header-spacer"></div>
        </div>

        {notifications.length > 0 && (
          <div className="NotificationList-markall-row">
            <span className="NotificationList-markall" onClick={markAllAsRead}>
              Mark all as read
            </span>
          </div>
        )}
        {/* Loading state */}
        {loading && (
          <div className="NotificationList-loading">
            <Spinner animation="border" style={{ color: "#1B9B82" }} />
          </div>
        )}

        {/* Error state */}
        {!loading && errorMessage && (
          <p className="text-center text-muted mt-4">{errorMessage}</p>
        )}

        {/* Empty state */}
        {!loading && !errorMessage && notifications.length === 0 && (
          <div className="NotificationList-empty">
            <Bell size={48} color="#1B9B82" />
            <p className="NotificationList-empty-text">No notifications yet</p>
          </div>
        )}

        {/* Notification items */}
        {!loading &&
          notifications.map((item) => (
            <div
              key={item.id}
              className={`NotificationList-item ${
                item.is_read ? "" : "NotificationList-item-unread"
              }`}
              onClick={() => markAsRead(item.id)}
            >
              <div
                className="NotificationList-icon-wrap"
                style={{ backgroundColor: item.color }}
              >
                {item.icon}
              </div>
              <div className="NotificationList-item-body">
                <div className="NotificationList-item-top">
                  <h6 className="NotificationList-item-title">{item.title}</h6>
                  {!item.is_read && (
                    <span className="NotificationList-dot"></span>
                  )}
                </div>
                <p className="NotificationList-item-desc">{item.description}</p>
                <span className="NotificationList-item-time">
                  {item.time_ago || item.created_at}
                </span>
              </div>
            </div>
          ))}
      </Container>
    </div>
  );
};

export default NotificationList;
